import React, { useState } from 'react';
import { TrendingDown, TrendingUp, DollarSign, CreditCard, Shield, AlertTriangle, CheckCircle, Percent, Calendar, BarChart3 } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, ComposedChart } from 'recharts';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const FinancialStructure = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Données pour les graphiques de comparaison sur 5 ans
    const structureData = [
        { 
            annee: '2020', 
            endettement: 68, 
            capitauxPropres: 350000, 
            dettesTotales: 238000, 
            capaciteRemboursement: 2.8,
            ratioAutonomie: 0.60,
            couvertureDettes: 1.85
        },
        { 
            annee: '2021', 
            endettement: 62, 
            capitauxPropres: 385000, 
            dettesTotales: 245000, 
            capaciteRemboursement: 3.2,
            ratioAutonomie: 0.65,
            couvertureDettes: 2.15
        },
        { 
            annee: '2022', 
            endettement: 58, 
            capitauxPropres: 420000, 
            dettesTotales: 245000, 
            capaciteRemboursement: 3.8,
            ratioAutonomie: 0.68,
            couvertureDettes: 2.45
        },
        { 
            annee: '2023', 
            endettement: 52, 
            capitauxPropres: 465000, 
            dettesTotales: 242000, 
            capaciteRemboursement: 4.2,
            ratioAutonomie: 0.72,
            couvertureDettes: 2.85
        },
        { 
            annee: '2024', 
            endettement: 45, 
            capitauxPropres: 520000, 
            dettesTotales: 234000, 
            capaciteRemboursement: 4.8,
            ratioAutonomie: 0.76,
            couvertureDettes: 3.25
        }
    ];

    // Répartition des dettes par type
    const repartitionDettes = [
        { name: 'Emprunts bancaires', value: 65, color: '#ef4444', montant: 152100 },
        { name: 'Dettes fournisseurs', value: 20, color: '#f59e0b', montant: 46800 },
        { name: 'Dettes fiscales', value: 10, color: '#06b6d4', montant: 23400 },
        { name: 'Autres dettes', value: 5, color: '#8b5cf6', montant: 11700 }
    ];

    // Évolution mensuelle des ratios 2024
    const evolutionRatios = [
        { mois: 'Jan', endettement: 48, autonomie: 0.73, liquidite: 2.2 },
        { mois: 'Fév', endettement: 47, autonomie: 0.74, liquidite: 2.4 },
        { mois: 'Mar', endettement: 46, autonomie: 0.75, liquidite: 2.5 },
        { mois: 'Avr', endettement: 46, autonomie: 0.75, liquidite: 2.3 },
        { mois: 'Mai', endettement: 45, autonomie: 0.76, liquidite: 2.6 },
        { mois: 'Juin', endettement: 45, autonomie: 0.76, liquidite: 2.7 },
        { mois: 'Juil', endettement: 45, autonomie: 0.76, liquidite: 2.8 },
        { mois: 'Août', endettement: 45, autonomie: 0.76, liquidite: 2.8 }
    ];

    // Comparaison benchmarks sectoriels
    const benchmarkData = [
        { indicateur: 'Endettement', entreprise: 45, secteur: 62, optimal: 50 },
        { indicateur: 'Autonomie', entreprise: 76, secteur: 58, optimal: 65 },
        { indicateur: 'Capacité', entreprise: 4.8, secteur: 3.2, optimal: 4.0 },
        { indicateur: 'Couverture', entreprise: 3.25, secteur: 2.1, optimal: 2.5 }
    ];

    // Données actuelles 2024
    const currentYear = {
        endettement: 45,
        capitauxPropres: 520000,
        dettesTotales: 234000,
        capaciteRemboursement: 4.8,
        ratioAutonomie: 0.76,
        couvertureDettes: 3.25,
        fraisFinanciers: 15600,
        resultatFinancier: -8200
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatPercent = (value) => {
        return `${value}%`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const calculatePercentage = (current, previous) => {
        if (!previous) return 0;
        return ((current - previous) / previous * 100).toFixed(1);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.name.includes('XOF') ? formatCurrency(entry.value) : 
                              entry.name.includes('%') ? formatPercent(entry.value) : entry.value}`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomPercentTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip">
                    <p className="tooltip-label">{`Année ${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="tooltip-value">
                            {`${entry.name}: ${entry.value}%`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getStatusColor = (value, type) => {
        switch (type) {
            case 'endettement':
                return value <= 50 ? '#10b981' : value <= 70 ? '#f59e0b' : '#ef4444';
            case 'autonomie':
                return value >= 0.7 ? '#10b981' : value >= 0.5 ? '#f59e0b' : '#ef4444';
            case 'capacite':
                return value >= 4 ? '#10b981' : value >= 2.5 ? '#f59e0b' : '#ef4444';
            default:
                return '#64748b';
        }
    };

    const getStatusText = (value, type) => {
        switch (type) {
            case 'endettement':
                return value <= 50 ? 'Optimal' : value <= 70 ? 'Acceptable' : 'Élevé';
            case 'autonomie':
                return value >= 0.7 ? 'Excellent' : value >= 0.5 ? 'Correct' : 'Faible';
            case 'capacite':
                return value >= 4 ? 'Très bonne' : value >= 2.5 ? 'Correcte' : 'Insuffisante';
            default:
                return 'Normal';
        }
    };

    return (
        <>
            <div className="app-container">
                <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

                <div className="main-wrapper">
                    <Navbar onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} />

                    <main className="main-content">
                        {/* Header Section */}
                        <div className="page-header">
                            <div className="header-content">
                                <div className="header-text">
                                    <h1 className="page-title">Structure Financière</h1>
                                    <p className="page-subtitle">Analyse de l'endettement, de l'autonomie financière et des capacités de remboursement</p>
                                </div>
                            </div>
                        </div>

                        {/* KPIs Cards */}
                        <div className="kpis-grid">
                            <div className="kpi-card primary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Taux d'Endettement</h3>
                                        <p className="kpi-value">{formatPercent(currentYear.endettement)}</p>
                                        <div className="kpi-status" style={{ color: getStatusColor(currentYear.endettement, 'endettement') }}>
                                            <CheckCircle size={16} />
                                            {getStatusText(currentYear.endettement, 'endettement')}
                                        </div>
                                    </div>
                                    <div className="kpi-icon" style={{ color: getStatusColor(currentYear.endettement, 'endettement') }}>
                                        <Percent size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card success">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Capitaux Propres</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.capitauxPropres)}</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.capitauxPropres, structureData[3].capitauxPropres)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <DollarSign size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card info">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Ratio d'Autonomie Financière</h3>
                                        <p className="kpi-value">{(currentYear.ratioAutonomie * 100).toFixed(0)}%</p>
                                        <div className="kpi-status" style={{ color: getStatusColor(currentYear.ratioAutonomie, 'autonomie') }}>
                                            <CheckCircle size={16} />
                                            {getStatusText(currentYear.ratioAutonomie, 'autonomie')}
                                        </div>
                                    </div>
                                    <div className="kpi-icon">
                                        <Shield size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card warning">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Dettes Totales</h3>
                                        <p className="kpi-value">{formatCurrency(currentYear.dettesTotales)}</p>
                                        <span className="kpi-trend positive">
                                            -{Math.abs(calculatePercentage(currentYear.dettesTotales, structureData[3].dettesTotales))}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <CreditCard size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card secondary">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Capacité de Remboursement</h3>
                                        <p className="kpi-value">{currentYear.capaciteRemboursement.toFixed(1)} ans</p>
                                        <div className="kpi-status" style={{ color: getStatusColor(currentYear.capaciteRemboursement, 'capacite') }}>
                                            <CheckCircle size={16} />
                                            {getStatusText(currentYear.capaciteRemboursement, 'capacite')}
                                        </div>
                                    </div>
                                    <div className="kpi-icon">
                                        <TrendingDown size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>

                            <div className="kpi-card danger">
                                <div className="kpi-content">
                                    <div className="kpi-info">
                                        <h3 className="kpi-title">Couverture des Dettes</h3>
                                        <p className="kpi-value">{currentYear.couvertureDettes}x</p>
                                        <span className="kpi-trend positive">
                                            +{calculatePercentage(currentYear.couvertureDettes, structureData[3].couvertureDettes)}%
                                        </span>
                                    </div>
                                    <div className="kpi-icon">
                                        <BarChart3 size={36} />
                                    </div>
                                </div>
                                <div className="kpi-gradient"></div>
                            </div>
                        </div>

                        {/* Charts Section */}
                        <div className="charts-section">
                            {/* Evolution du Taux d'Endettement sur 5 ans */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution de la Structure Financière (5 dernières années)</h3>
                                    <div className="chart-badge success">Amélioration continue</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={400}>
                                        <ComposedChart data={structureData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis yAxisId="left"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                label={{ value: 'Montant (XOF)', angle: -90, position: 'insideLeft' }}
                                                tickFormatter={(value) => `${(value / 1000)}kXOF`}
                                            />
                                            <YAxis yAxisId="right" orientation="right"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                label={{ value: 'Endettement (%)', angle: 90, position: 'insideRight' }}
                                                tickFormatter={(value) => `${value}%`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="capitauxPropres"
                                                name="Capitaux Propres (XOF)"
                                                fill="#10b981"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="dettesTotales"
                                                name="Dettes Totales (XOF)"
                                                fill="#ef4444"
                                                radius={[4, 4, 0, 0]}
                                                opacity={0.8}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="endettement"
                                                name="Taux d'Endettement (%)"
                                                stroke="#3b82f6"
                                                strokeWidth={3}
                                                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8 }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Répartition des Dettes */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Répartition des Dettes par Type</h3>
                                    <div className="chart-badge warning">Structure analysée</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <PieChart>
                                            <Pie
                                                data={repartitionDettes}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value }) => `${name}: ${value}%`}
                                                labelLine={false}
                                            >
                                                {repartitionDettes.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value, name, props) => [
                                                `${value}% (${formatCurrency(props.payload.montant)})`, name
                                            ]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Evolution des Ratios Mensuels */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution des Ratios 2024</h3>
                                    <div className="chart-badge secondary">Suivi mensuel</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <LineChart data={evolutionRatios}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="mois"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis yAxisId="left"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${value}%`}
                                            />
                                            <YAxis yAxisId="right" orientation="right"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${value}`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Line
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="endettement"
                                                name="Endettement (%)"
                                                stroke="#ef4444"
                                                strokeWidth={3}
                                                dot={{ fill: '#ef4444', strokeWidth: 2, r: 5 }}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="autonomie"
                                                name="Autonomie"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                dot={{ fill: '#10b981', strokeWidth: 2, r: 5 }}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="liquidite"
                                                name="Liquidité"
                                                stroke="#3b82f6"
                                                strokeWidth={3}
                                                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Capacité de Remboursement sur 5 ans */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Capacité de Remboursement</h3>
                                    <div className="chart-badge success">Performance excellente</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <AreaChart data={structureData}>
                                            <defs>
                                                <linearGradient id="colorCapacite" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                label={{ value: 'Années', angle: -90, position: 'insideLeft' }}
                                                tickFormatter={(value) => `${value} ans`}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Area
                                                type="monotone"
                                                dataKey="capaciteRemboursement"
                                                stroke="#8b5cf6"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorCapacite)"
                                                dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#8b5cf6', strokeWidth: 2 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Comparaison Benchmark Sectoriel */}
                            <div className="chart-card">
                                <div className="chart-header">
                                    <h3 className="chart-title">Comparaison Sectorielle</h3>
                                    <div className="chart-badge info">Positionnement excellent</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={benchmarkData} layout="horizontal">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis type="number"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis type="category" dataKey="indicateur"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                width={80}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Bar
                                                dataKey="entreprise"
                                                name="Votre Entreprise"
                                                fill="#10b981"
                                                radius={[0, 4, 4, 0]}
                                                opacity={0.9}
                                            />
                                            <Bar
                                                dataKey="secteur"
                                                name="Moyenne Sectorielle"
                                                fill="#64748b"
                                                radius={[0, 4, 4, 0]}
                                                opacity={0.7}
                                            />
                                            <Bar
                                                dataKey="optimal"
                                                name="Niveau Optimal"
                                                fill="#3b82f6"
                                                radius={[0, 4, 4, 0]}
                                                opacity={0.8}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Ratio d'Autonomie sur 5 ans */}
                            <div className="chart-card full-width">
                                <div className="chart-header">
                                    <h3 className="chart-title">Évolution du Ratio d'Autonomie Financière</h3>
                                    <div className="chart-badge success">Progression constante</div>
                                </div>
                                <div className="chart-content">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={structureData}>
                                            <defs>
                                                <linearGradient id="colorAutonomie" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="annee"
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                                                domain={[0.5, 0.8]}
                                            />
                                            <Tooltip formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Ratio d\'Autonomie']} />
                                            <Area
                                                type="monotone"
                                                dataKey="ratioAutonomie"
                                                stroke="#10b981"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorAutonomie)"
                                                dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
                                                activeDot={{ r: 8, stroke: '#10b981', strokeWidth: 2 }}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Commentaires du Cabinet */}
                        <div className="commentaires-section">
                            <div className="commentaires-card">
                                <div className="commentaires-header">
                                    <h3 className="commentaires-title">Commentaires du Cabinet CINEF-EC</h3>
                                    <div className="commentaires-date">
                                        Dernière mise à jour: {formatDate('2024-08-20')}
                                    </div>
                                </div>
                                <div className="commentaires-content">
                                    <div className="commentaire-item">
                                        <h4 className="commentaire-title">Analyse de l'Endettement</h4>
                                        <p className="commentaire-text">
                                            Le taux d'endettement de {formatPercent(currentYear.endettement)} témoigne d'une structure financière très saine.
                                            Cette diminution progressive depuis 2020 (68%) révèle une stratégie de désendettement réussie et une 
                                            capacité d'autofinancement croissante. Le niveau actuel se situe bien en deçà du seuil d'alerte sectoriel.
                                        </p>
                                    </div>

                                 <div className="commentaire-item">
                        <h4 className="commentaire-title">Autonomie Financière Renforcée</h4>
                        <p className="commentaire-text">
                            Le ratio d'autonomie financière de {(currentYear.ratioAutonomie * 100).toFixed(0)}% place l'entreprise dans une 
                            position de force remarquable. Cette progression constante depuis 2020 (60%) démontre une capacité 
                            d'autofinancement solide et une maîtrise des investissements. La part croissante des capitaux propres 
                            dans le financement confère une grande flexibilité stratégique.
                        </p>
                    </div>

                    <div className="commentaire-item">
                        <h4 className="commentaire-title">Capacité de Remboursement Optimale</h4>
                        <p className="commentaire-text">
                            Avec une capacité de remboursement de {currentYear.capaciteRemboursement.toFixed(1)} années, l'entreprise 
                            présente un profil de risque très favorable. Cette amélioration continue traduit une génération de 
                            cash-flow opérationnel robuste. Le ratio de couverture des dettes de {currentYear.couvertureDettes}x 
                            confirme la solidité de la structure financière.
                        </p>
                    </div>

                    <div className="commentaire-item">
                        <h4 className="commentaire-title">Recommandations Stratégiques</h4>
                        <p className="commentaire-text">
                            La structure financière actuelle offre d'excellentes perspectives pour soutenir la croissance. 
                            Nous recommandons de maintenir le niveau d'endettement actuel tout en optimisant la structure 
                            de dettes. Les conditions financières favorables permettent d'envisager des investissements 
                            stratégiques sans compromettre l'équilibre financier.
                        </p>
                    </div>

                    <div className="commentaire-item success">
                        <h4 className="commentaire-title">Points Forts Identifiés</h4>
                        <ul className="commentaire-list">
                            <li>Réduction continue du taux d'endettement (-23 points en 5 ans)</li>
                            <li>Croissance soutenue des capitaux propres (+48% depuis 2020)</li>
                            <li>Amélioration significative de la capacité de remboursement</li>
                            <li>Position supérieure aux standards sectoriels</li>
                            <li>Diversification équilibrée des sources de financement</li>
                        </ul>
                    </div>

                    <div className="commentaire-item warning">
                        <h4 className="commentaire-title">Points de Vigilance</h4>
                        <ul className="commentaire-list">
                            <li>Surveiller l'évolution des taux d'intérêt sur les emprunts variables</li>
                            <li>Optimiser le délai de paiement des dettes fournisseurs</li>
                            <li>Maintenir une réserve de liquidité pour les opportunités d'investissement</li>
                            <li>Anticiper les échéances de renouvellement des lignes de crédit</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        {/* Tableau de Bord des Ratios Clés */}
        <div className="ratios-dashboard">
            <div className="dashboard-card">
                <div className="dashboard-header">
                    <h3 className="dashboard-title">Tableau de Bord - Ratios Clés 2024</h3>
                    <div className="dashboard-badge success">Situation Excellent</div>
                </div>
                <div className="dashboard-content">
                    <div className="ratios-grid">
                        <div className="ratio-item">
                            <div className="ratio-label">Taux d'Endettement</div>
                            <div className="ratio-value" style={{ color: getStatusColor(currentYear.endettement, 'endettement') }}>
                                {formatPercent(currentYear.endettement)}
                            </div>
                            <div className="ratio-status" style={{ color: getStatusColor(currentYear.endettement, 'endettement') }}>
                                {getStatusText(currentYear.endettement, 'endettement')}
                            </div>
                            <div className="ratio-benchmark">Sectoriel: 62%</div>
                        </div>

                        <div className="ratio-item">
                            <div className="ratio-label">Autonomie Financière</div>
                            <div className="ratio-value" style={{ color: getStatusColor(currentYear.ratioAutonomie, 'autonomie') }}>
                                {(currentYear.ratioAutonomie * 100).toFixed(0)}%
                            </div>
                            <div className="ratio-status" style={{ color: getStatusColor(currentYear.ratioAutonomie, 'autonomie') }}>
                                {getStatusText(currentYear.ratioAutonomie, 'autonomie')}
                            </div>
                            <div className="ratio-benchmark">Sectoriel: 58%</div>
                        </div>

                        <div className="ratio-item">
                            <div className="ratio-label">Capacité Remboursement</div>
                            <div className="ratio-value" style={{ color: getStatusColor(currentYear.capaciteRemboursement, 'capacite') }}>
                                {currentYear.capaciteRemboursement.toFixed(1)} ans
                            </div>
                            <div className="ratio-status" style={{ color: getStatusColor(currentYear.capaciteRemboursement, 'capacite') }}>
                                {getStatusText(currentYear.capaciteRemboursement, 'capacite')}
                            </div>
                            <div className="ratio-benchmark">Sectoriel: 3.2 ans</div>
                        </div>

                        <div className="ratio-item">
                            <div className="ratio-label">Couverture Dettes</div>
                            <div className="ratio-value" style={{ color: '#10b981' }}>
                                {currentYear.couvertureDettes}x
                            </div>
                            <div className="ratio-status" style={{ color: '#10b981' }}>
                                Excellent
                            </div>
                            <div className="ratio-benchmark">Sectoriel: 2.1x</div>
                        </div>

                        <div className="ratio-item">
                            <div className="ratio-label">Frais Financiers</div>
                            <div className="ratio-value" style={{ color: '#f59e0b' }}>
                                {formatCurrency(currentYear.fraisFinanciers)}
                            </div>
                            <div className="ratio-status" style={{ color: '#f59e0b' }}>
                                Contrôlé
                            </div>
                            <div className="ratio-benchmark">-12% vs 2023</div>
                        </div>

                        <div className="ratio-item">
                            <div className="ratio-label">Résultat Financier</div>
                            <div className="ratio-value" style={{ color: currentYear.resultatFinancier >= 0 ? '#10b981' : '#ef4444' }}>
                                {formatCurrency(currentYear.resultatFinancier)}
                            </div>
                            <div className="ratio-status" style={{ color: currentYear.resultatFinancier >= 0 ? '#10b981' : '#ef4444' }}>
                                {currentYear.resultatFinancier >= 0 ? 'Positif' : 'Négatif'}
                            </div>
                            <div className="ratio-benchmark">Impact maîtrisé</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Actions Recommandées */}
        <div className="actions-section">
            <div className="actions-card">
                <div className="actions-header">
                    <h3 className="actions-title">Plan d'Actions Recommandé</h3>
                    <div className="actions-badge primary">Priorités 2024-2025</div>
                </div>
                <div className="actions-content">
                    <div className="actions-grid">
                        <div className="action-item high">
                            <div className="action-priority">Priorité Haute</div>
                            <h4 className="action-title">Optimisation de la Structure de Dettes</h4>
                            <p className="action-description">
                                Négocier une réduction des taux sur les emprunts bancaires existants et 
                                diversifier les sources de financement pour réduire les coûts financiers.
                            </p>
                            <div className="action-timeline">Échéance: Q4 2024</div>
                            <div className="action-impact">Impact estimé: -15% frais financiers</div>
                        </div>

                        <div className="action-item medium">
                            <div className="action-priority">Priorité Moyenne</div>
                            <h4 className="action-title">Renforcement des Fonds Propres</h4>
                            <p className="action-description">
                                Capitaliser une partie des bénéfices non distribués pour accroître davantage 
                                l'autonomie financière et soutenir les projets de développement.
                            </p>
                            <div className="action-timeline">Échéance: Q1 2025</div>
                            <div className="action-impact">Impact estimé: +5% autonomie</div>
                        </div>

                        <div className="action-item low">
                            <div className="action-priority">Priorité Basse</div>
                            <h4 className="action-title">Optimisation du BFR</h4>
                            <p className="action-description">
                                Améliorer la gestion du cycle d'exploitation pour libérer des liquidités 
                                supplémentaires et réduire le besoin de financement externe.
                            </p>
                            <div className="action-timeline">Échéance: Q2 2025</div>
                            <div className="action-impact">Impact estimé: +10% trésorerie</div>
                        </div>

                        <div className="action-item info">
                            <div className="action-priority">Suivi Continu</div>
                            <h4 className="action-title">Monitoring des Indicateurs</h4>
                            <p className="action-description">
                                Mettre en place un tableau de bord mensuel pour suivre l'évolution des ratios 
                                et anticiper les déviations par rapport aux objectifs fixés.
                            </p>
                            <div className="action-timeline">En continu</div>
                            <div className="action-impact">Impact: Pilotage optimisé</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Footer avec signature */}
        <div className="report-footer">
            <div className="footer-content">
                <div className="footer-signature">
                    <div className="signature-line">
                        <strong>Cabinet CINEF-EC</strong>
                    </div>
                    <div className="signature-line">
                        Expert-Comptable & Conseil en Gestion
                    </div>
                    <div className="signature-line">
                        Rapport généré le {formatDate(new Date().toISOString())}
                    </div>
                </div>
                <div className="footer-contact">
                    <div className="contact-info">
                        <strong>Contact:</strong> contact@cinef-ec.com | Tel: +229 XX XX XX XX
                    </div>
                </div>
            </div>
        </div>

                    </main>
                </div>
            </div>

            <style jsx>{`
                /* Styles de base pour la page Structure Financière */

/* Container principal */
.app-container {
    display: flex;
    min-height: 100vh;
    background-color: #f8fafc;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.main-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin-left: 320px;
    transition: margin-left 0.3s ease;
}

.main-content {
    flex: 1;
    padding: 24px;
    overflow-x: hidden;
}

/* Header de page */
.page-header {
                    margin-bottom: 32px;
                }

 .header-content {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

.page-title {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 0 0 8px 0;
                    background: linear-gradient(135deg, #dc3545, #008A44);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    line-height: 1.2;
                    letter-spacing: -0.8px;
                    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

.page-subtitle {
                    color: rgba(255, 255, 255, 0.8);
                    margin: 0;
                    font-size: 16px;
                    font-weight: 500;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                }
    .header-text {
     flex: 1;
    }


/* Grille des KPIs */
.kpis-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
}

/* Cartes KPI */
.kpi-card {
    position: relative;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
    overflow: hidden;
}

.kpi-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.kpi-card.primary { border-left: 4px solid #3b82f6; }
.kpi-card.success { border-left: 4px solid #10b981; }
.kpi-card.info { border-left: 4px solid #06b6d4; }
.kpi-card.warning { border-left: 4px solid #f59e0b; }
.kpi-card.secondary { border-left: 4px solid #8b5cf6; }
.kpi-card.danger { border-left: 4px solid #ef4444; }

.kpi-content {
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.kpi-info {
    flex: 1;
}

.kpi-title {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    margin: 0 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.kpi-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    line-height: 1.2;
}

.kpi-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.kpi-trend {
    font-size: 13px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.kpi-trend.positive {
    background: #dcfce7;
    color: #166534;
}

.kpi-trend.negative {
    background: #fee2e2;
    color: #991b1b;
}

.kpi-icon {
    color: #64748b;
    opacity: 0.6;
}

.kpi-gradient {
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    z-index: 1;
}

/* Section des graphiques */
.charts-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
}

.chart-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;
}

.chart-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.chart-card.full-width {
    grid-column: 1 / -1;
}

.chart-header {
    padding: 20px 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.chart-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.chart-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.chart-badge.success { background: #dcfce7; color: #166534; }
.chart-badge.warning { background: #fef3c7; color: #92400e; }
.chart-badge.info { background: #dbeafe; color: #1e40af; }
.chart-badge.secondary { background: #e9d5ff; color: #7c2d12; }
.chart-badge.primary { background: #dbeafe; color: #1d4ed8; }

.chart-content {
    padding: 24px;
}

/* Tooltips personnalisés */
.custom-tooltip {
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border: none;
}

.tooltip-label {
    font-weight: 600;
    margin-bottom: 8px;
    color: #e2e8f0;
    font-size: 14px;
}

.tooltip-value {
    margin: 4px 0;
    font-size: 13px;
    font-weight: 500;
}

/* Section commentaires */
.commentaires-section {
    margin: 32px 0;
}

.commentaires-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.commentaires-header {
    padding: 24px;
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.commentaires-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
}

.commentaires-date {
    font-size: 14px;
    opacity: 0.8;
}

.commentaires-content {
    padding: 32px;
}

.commentaire-item {
    background: #f8fafc;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    border-left: 4px solid #e2e8f0;
    transition: all 0.3s ease;
}

.commentaire-item:last-child {
    margin-bottom: 0;
}

.commentaire-item:hover {
    background: #f1f5f9;
    transform: translateX(4px);
}

.commentaire-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px 0;
}

.commentaire-text {
    font-size: 15px;
    line-height: 1.7;
    color: #475569;
    margin: 0;
}

/* Styles spécifiques pour success et warning déjà dans le JSX */

.commentaire-list {
    list-style: none;
    padding: 0;
    margin: 12px 0 0 0;
}

.commentaire-list li {
    padding: 8px 0;
    padding-left: 20px;
    position: relative;
    font-size: 14px;
    line-height: 1.6;
    color: #475569;
}

.commentaire-list li:before {
    content: "•";
    color: #3b82f6;
    font-weight: bold;
    position: absolute;
    left: 0;
}

/* Dashboard des ratios - styles dans le JSX */

/* Actions recommandées - styles dans le JSX */

/* Footer du rapport - styles dans le JSX */

/* Responsive Design */
@media (max-width: 1200px) {
    .charts-section {
        grid-template-columns: 1fr;
    }
    
    .chart-card.full-width {
        grid-column: 1;
    }
}

@media (max-width: 768px) {
    .main-content {
        padding: 16px;
    }
    
    .page-header {
        padding: 24px 20px;
        margin-bottom: 24px;
    }
    
    .page-title {
        font-size: 2rem;
    }
    
    .page-subtitle {
        font-size: 1rem;
    }
    
    .kpis-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .kpi-card {
        padding: 20px;
    }
    
    .kpi-value {
        font-size: 1.75rem;
    }
    
    .charts-section {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .chart-content {
        padding: 16px;
    }
    
    .commentaires-content {
        padding: 20px;
    }
    
    .commentaire-item {
        padding: 20px;
    }
}

@media (max-width: 480px) {
    .main-content {
        padding: 12px;
    }
    
    .page-header {
        padding: 20px 16px;
    }
    
    .page-title {
        font-size: 1.75rem;
    }
    
    .kpi-card {
        padding: 16px;
    }
    
    .kpi-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    
    .kpi-icon {
        align-self: flex-end;
    }
    
    .chart-header {
        padding: 16px 20px;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    
    .chart-content {
        padding: 12px;
    }
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.kpi-card {
    animation: slideIn 0.6s ease-out;
}

.chart-card {
    animation: fadeIn 0.8s ease-out;
}

.commentaire-item {
    animation: slideIn 0.6s ease-out;
}

/* Styles pour les scrollbars */
::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Print styles */
@media print {
    .sidebar,
    .navbar {
        display: none !important;
    }
    
    .main-wrapper {
        margin-left: 0 !important;
    }
    
    .kpi-card,
    .chart-card,
    .commentaires-card {
        box-shadow: none;
        border: 1px solid #e2e8f0;
        break-inside: avoid;
    }
    
    .page-header {
        background: #667eea !important;
        -webkit-print-color-adjust: exact;
    }
}
            `}</style>
        </>
    );
};

export default FinancialStructure;